import { FC } from "react";
import { Control, Controller } from "react-hook-form";
import InputField from "app/components/common/inputs/InputField";
import FormGroup from "app/components/common/inputs/FormGroup";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import { stateOfIssueOptions } from "app/utils/staticValues";
import CustomDatePicker from "app/components/common/inputs/CustomDatePicker";
import RadioButton from "app/components/common/buttons/RadioButton";
import TextArea from "app/components/common/inputs/TextArea";
import SelectField from "app/components/common/inputs/SelectField";

interface Props {
  control: Control<any>;
  errors: any;
  applicantName: string;
  knownByOtherName: string;
  otherNameDesc: string;
  stateOfIssue: string;
  drivingLicense: string;
  dateOfBirth: string;
  expiry: string;
}

const DynamicFormTwo: FC<Props> = ({
  control,
  errors,
  applicantName,
  knownByOtherName,
  otherNameDesc,
  stateOfIssue,
  drivingLicense,
  dateOfBirth,
  expiry,
}) => {
  const getErrorMessage = (fieldName: string) => {
    const fieldNames = fieldName.split(".");
    return fieldNames.reduce((acc, key) => acc && acc[key], errors);
  };

  return (
    <>
      <div className="text-left mb-5">
        <h6 className="font-Mulish text-nowrap text-base text-lightBlue mb-4">
          Is applicant known by any other name?
        </h6>
        <div className="flex gap-5">
          <Controller
            name={knownByOtherName}
            control={control}
            rules={{ required: "Please select an option" }}
            render={({ field: { value, onChange } }) => (
              <div className="flex gap-5">
                <RadioButton
                  id={`${knownByOtherName}_yes`}
                  name={knownByOtherName}
                  value="yes"
                  label="Yes"
                  checked={value === "yes"}
                  onChange={() => onChange("yes")}
                />
                <RadioButton
                  id={`${knownByOtherName}_no`}
                  name={knownByOtherName}
                  value="no"
                  label="No"
                  checked={value === "no"}
                  onChange={() => onChange("no")}
                />
              </div>
            )}
          />
        </div>
        {errors?.knownByOtherName && (
          <ErrorsMessage
            title={getErrorMessage(knownByOtherName)?.message as string}
            className="!text-left"
          />
        )}
      </div>

      {applicantName === "yes" && (
        <FormGroup>
          <Controller
            name={otherNameDesc}
            control={control}
            rules={{
              validate: (value) =>
                applicantName === "yes" && !value
                  ? "Please provide more details"
                  : true,
            }}
            render={({ field }) => (
              <TextArea
                control={control}
                label="If yes, please provide more details"
                {...field}
                error={getErrorMessage(otherNameDesc)?.message as string}
              />
            )}
          />
        </FormGroup>
      )}

      <FormGroup>
        <SelectField
          label="State of issue"
          name={stateOfIssue}
          control={control}
          options={stateOfIssueOptions}
          placeholder="Please select state of issue"
          error={getErrorMessage(stateOfIssue)?.message as string}
          required={true}
          className="flex-1"
        />
        <InputField
          label="Driver's license number"
          name={drivingLicense}
          control={control}
          type="number"
          required={true}
          placeholder="000000000"
          rules={{
            required: "Driving license number is required",
            minLength: {
              value: 8,
              message: "License number must be at least 8 characters",
            },
            maxLength: {
              value: 12,
              message: "License number must be at most 12 characters",
            },
            pattern: {
              value: /^[0-9]+$/,
              message: "Invalid format — only numbers are allowed",
            },
          }}
          error={getErrorMessage(drivingLicense)?.message as string}
          className="flex-1"
        />
      </FormGroup>
      <FormGroup>
        <CustomDatePicker
          name={dateOfBirth}
          control={control}
          label="Date of birth"
          placeholder="DD/MM/YYYY"
          // validateAge={true}
          rules={{ required: "Date is required" }}
          className="flex-1"
          maxDate={new Date()}
        />
        <CustomDatePicker
          name={expiry}
          control={control}
          label="Expiry date"
          placeholder="DD/MM/YYYY"
          rules={{ required: "Date is required" }}
          className="flex-1"
          validateFutureDate={true}
          minDate={new Date()}
        />
      </FormGroup>
    </>
  );
};

export default DynamicFormTwo;
